import { getDefaultPerilValues } from '.';
import { FieldConfiguration } from '../../../api/ConfigApi';
import { HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import {
	FieldGroupsByLocatorMap,
	FieldValuesMap,
	Peril,
	PerilCharacteristics,
} from '../../../types/policy';
import {
	getCharacteristicAtTime_T_,
	getExposureConfig,
	getPerilConfigFromExposureConfig,
	isTimestampWithinExclusiveRange,
	isTimestampWithinInclusiveRange,
	removeLimitsFromPerilConfig,
} from '../../../util/util';
import { FormBuilder, FormShape, PolicyFormTransformer } from '../types';
import { getPerilFields } from '../utils';

const getPerilCharacteristicsRequired = (
	timestamp: string,
	selectedPeril: Peril,
): PerilCharacteristics => {
	const activePerilCharacteristic: PerilCharacteristics =
		selectedPeril.characteristics.find((c) => {
			return (
				!c.replacedTimestamp &&
				isTimestampWithinExclusiveRange(
					timestamp,
					c.coverageStartTimestamp,
					c.coverageEndTimestamp,
				)
			);
		});
	if (activePerilCharacteristic) {
		return activePerilCharacteristic;
	} else {
		const allActiveCharacteristics = selectedPeril.characteristics.filter(
			(c) => !c.replacedTimestamp,
		);
		if (allActiveCharacteristics.length) {
			const firstActiveAfterTimestamp = allActiveCharacteristics.find(
				(c) => +c.coverageStartTimestamp > parseInt(timestamp),
			);
			const lastActiveBeforeTimestamp = allActiveCharacteristics
				.reverse()
				.find((c) => +c.coverageEndTimestamp <= parseInt(timestamp));
			return firstActiveAfterTimestamp || lastActiveBeforeTimestamp;
		} else {
			// check if there is inactive characteristic at timestamp
			let inactiveCharacteristic = selectedPeril.characteristics.find((c) =>
				isTimestampWithinInclusiveRange(
					timestamp,
					c.coverageStartTimestamp,
					c.coverageEndTimestamp,
				),
			);
			if (inactiveCharacteristic) {
				return inactiveCharacteristic;
			} else {
				// grab first characteristic after timestamp
				const firstCharAfterTimestamp = selectedPeril.characteristics.find(
					(c) => +c.coverageStartTimestamp > parseInt(timestamp),
				);
				// grab last characteristic before timestamp
				const lastCharBeforeTimestamp = selectedPeril.characteristics
					.reverse()
					.find((c) => +c.coverageEndTimestamp <= parseInt(timestamp));
				return firstCharAfterTimestamp || lastCharBeforeTimestamp;
			}
		}
	}
};

const getQuotePolicyExposuresForm = (props: PolicyFormTransformer) => {
	const exposureForm = {} as FormShape<FormBuilder>;
	const { policyData, authData } = props;
	const { policy, policyViewTimestamp, productConfig, selectedQuote } =
		policyData;
	const quoteExposures = selectedQuote?.exposures;
	const isPolicyIssued = !!policy.issuedTimestamp;
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const exposures = isPolicyIssued ? policy.exposures : quoteExposures;

	for (const [, exposure] of exposures.entries()) {
		const currentExposureConfiguration = getExposureConfig(
			productConfig,
			exposure,
		);
		const currentExposureCharacteristics = policy.issuedTimestamp
			? getCharacteristicAtTime_T_(
					exposure.characteristics,
					parseInt(policyViewTimestamp, 10),
			  )
			: exposure.characteristics[0];

		exposureForm[exposure.locator] = {
			externalDataCalls: currentExposureConfiguration.externalDataCalls,
			mediaByLocator: currentExposureCharacteristics.mediaByLocator,
			fields: currentExposureConfiguration.fields,
			fieldGroupsByLocator: currentExposureCharacteristics.fieldGroupsByLocator,
			values: currentExposureCharacteristics.fieldValues,
		} as FormShape<FormBuilder>;

		// Add each exposure's perils to the default values object
		exposure.perils.forEach((selectedPeril) => {
			let currentPerilConfiguration = getPerilConfigFromExposureConfig(
				currentExposureConfiguration,
				selectedPeril,
			);
			// Remove limit fields from peril config if FF is enabled
			if (hidePerilLimitsEnabled) {
				currentPerilConfiguration = removeLimitsFromPerilConfig(
					currentPerilConfiguration,
				);
			}

			const currentPerilCharacteristics = isPolicyIssued
				? getPerilCharacteristicsRequired(policyViewTimestamp, selectedPeril)
				: getPerilCharacteristicsRequired(
						policy.characteristics[0].startTimestamp,
						selectedPeril,
				  );

			const fields = [
				...currentPerilConfiguration.fields,
			] as FieldConfiguration[];
			const fieldGroupsByLocator = {
				...currentPerilCharacteristics?.fieldGroupsByLocator,
			} as FieldGroupsByLocatorMap;
			let values: FieldValuesMap = {
				...currentPerilCharacteristics?.fieldValues,
			};

			// If limits are shown, get default values and append limits fields before fieldValues
			if (!hidePerilLimitsEnabled) {
				values = {
					...values,
					...getDefaultPerilValues(currentPerilCharacteristics),
				} as FieldValuesMap;

				// Create a break after the default fields
				if (fields.length > 0) {
					fields[0].newline = true;
				}
				// Move all of the peril default fields ahead of the added fields
				fields.unshift(...getPerilFields(policy?.currency));
			}

			const perilForm = {
				fields,
				fieldGroupsByLocator,
				values,
				mediaByLocator: currentPerilCharacteristics.mediaByLocator,
			} as FormBuilder;
			const perilLocator = selectedPeril.locator;
			(exposureForm[exposure.locator] as FormShape<FormBuilder>)[perilLocator] =
				perilForm;
		});
	}
	return exposureForm as FormShape<FormBuilder>;
};

export default getQuotePolicyExposuresForm;
